<template>
   <BRow>
        <BCol v-for="(block, i) in blocks" :key="i"  
               :xl="xl" 
               :lg="lg"
               :md="md">
        <section class="pbk-card">
          <header class="pbk-head">
            <nuxt-link class="pbk-title-link" :to="routePath({name:'cena-na-pijacama-products',params:{products: viewAll(block.item)}})">
              <component :is="titleTag" class="pbk-title">{{ langCategoryTitle(block.item) }}</component>
            </nuxt-link>
            <nuxt-link class="pbk-all" :to="routePath({name:'cena-na-pijacama-products',params:{products: viewAll(block.item)}})">
              pogledaj sve
            </nuxt-link>
          </header>

          <ul class="pbk-list">
            <li v-for="(product, i) in block.products" :key="i">
              <nuxt-link class="pbk-link" :to="routePath({name:'cena-na-pijaci-singleproducts',params:{singleproducts: singleproductsPath(product)}})">
                <client-only>
                  <i :class="`esei icon-${product.img} esei-default avatar-block text-white bg-${product.color} p-1 pbk-icon`"></i>
                </client-only>
                <span class="pbk-link-text">{{ product.description }}</span>
                <span class="pbk-link-cta">
                  Pogledaj cene
                  <span class="pbk-link-arrow" aria-hidden="true">›</span>
                </span>
              </nuxt-link>
            </li>
          </ul>
        </section>
    </BCol>
</BRow>
 
</template>

<script setup>
import { BCol, BRow } from 'bootstrap-vue-next'

const { $srbTolat } = useNuxtApp()
const routePath = useRoutePath()
const dayjs = useDayjs()

 

const locale = ref('sr')

// ✅ lang kao computed (da reaguje kada promeniš jezik)
const lang = computed(() => locale.value)

const props = defineProps({
  // oznaka naslova kartice: "div" (obican tekst, bocne kolone) ili "h2"/"h3" kad je kartica glavni sadrzaj stranice
  titleTag: { type: String, default: "div" },
  searchable: { type: Boolean, default: false },
  cols: { type: Number, require: true },
  xl: { type: Number, require: true, default: 4 },
  lg: { type: Number, require: true, default: 4 },
  md: { type: Number, require: true, default: 6 },
  counter: { type: Number, require: true, default: 6 },
  from: { type: Number, require: true, default: 0 },
  display: { type: Boolean, default: true },
  display_avatar: { type: Boolean, default: true },
  display_avatar_food: { type: Boolean, default: true },
  others_page: { type: Boolean, default: false },
  fix_height: { type: Boolean, default: true },
  category_name_ads: String,
})

// u HTML (payload) ide samo ono sto ova instanca prikazuje: kartice iz svog opsega i po 6 proizvoda u kartici;
// stranica sa filterom (searchable) trazi sve, pa joj se nista ne skracuje
const slimStips = (raw) => {
  if (props.searchable) return raw

  const all = raw?.category?.category ?? []
  const shown = all.filter(x => x.display == props.display).slice(props.from, props.counter)
  const keys = new Set(shown.map(x => (lang.value === 'en' ? x.title_path_en : x.title_path)))
  const taken = {}

  const kept = (raw?.subcategory?.subcategory ?? []).filter((x) => {
    if (!keys.has(x.title_path) || x.food != false) return false
    taken[x.title_path] = (taken[x.title_path] ?? 0) + 1
    return taken[x.title_path] <= 6
  })

  return { category: { category: all }, subcategory: { subcategory: kept } }
}

// ✅ API fetch (SSR-safe); kljuc ima opseg kartica, da instance sa razlicitim opsegom ne dele skracene podatke
const stipsKey = () => `stips-items-${lang.value}-${props.searchable ? 'all' : `${props.display}-${props.from}-${props.counter}`}`

const { data: stips, pending, error } = await useAsyncData(
  stipsKey,
  () => $fetch('/api/stips/items', { params: { lang: lang.value } }),
  { watch: [lang], transform: slimStips }
)

// ✅ izvuci nizove sa fallback-om
const categories = computed(() => stips.value?.category?.category ?? [])
const subcategories = computed(() => stips.value?.subcategory?.subcategory ?? [])

const countBlock = (items, from, count) => {
  return items.slice(from, count)
}

const filterCategory = () => {
  // ✅ koristi categories iz API-ja
  return categories.value.filter(x => x.display == props.display)
}

const viewAll = (item) => {
  // ✅ zadrži postojeću logiku
  switch (lang.value) {
    case 'sr':
      return item.title_path.split(' ').join('-').toLowerCase()
    case 'en':
      return item.title_path_en.split(' ').join('-').toLowerCase()
    default:
      return item.title_path.split(' ').join('-').toLowerCase()
  }
}

const langCategoryTitle = (item) => {
  switch (lang.value) {
    case 'sr':
      return item.title
    case 'en':
      return item.title_en
    default:
      return item.title
  }
}

const filterSubcategory = (item) => {
  // ✅ sve dolazi iz API-ja (jedan subcategories niz zavisi od lang na serveru)
  // Za sr koristi item.title_path, za en koristi item.title_path_en
  const key = lang.value === 'en' ? item.title_path_en : item.title_path

  return subcategories.value.filter(x => x.title_path === key && x.food == false)
}

const langFilter = (item) => {
  return filterSubcategory(item).slice(0, 6)
}

// lokalni filter stranice (<ListFilter />): kartica ostaje ako naslov ili neki proizvod odgovara
// filtriranje samo kad stranica to trazi (:searchable="true"), da bočne liste ne reaguju na polje u glavnoj koloni
const listFilter = useListFilter()
const active = computed(() => props.searchable && listFilter.active.value)
const match = listFilter.match

const blocks = computed(() => countBlock(filterCategory(), props.from, props.counter)
  .map((item) => {
    if (!active.value) return { item, products: langFilter(item) }

    // sa aktivnim filterom nema ogranicenja broja proizvoda u kartici
    const all = filterSubcategory(item)

    return { item, products: match(langCategoryTitle(item)) ? all : all.filter((p) => match(p.description)) }
  })
  .filter((block) => !active.value || block.products.length > 0))

useListFilterHits(computed(() => (active.value ? blocks.value.reduce((n, b) => n + b.products.length, 0) : 1)), props.searchable)

const singleproductsPath = (product) => {
  return (
    product.description_path.split(' ').join('-').toLowerCase() +
    '-' +
    product.value_path.split(' ').join('-').toLowerCase() +
    '-' +
    product.catgeory_path +
    '-' +
    dayjs().year()
  )
}
</script>


<style>

</style>
